import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { formatDistanceToNow } from 'date-fns'
import { nl } from 'date-fns/locale'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2,
  Calendar,
  User,
  Database,
  FileIcon,
  Link2,
  MessageSquare,
  Zap,
  Users,
  ActivitySquare,
} from 'lucide-react'
import { usePracticeCaseArticles } from '@/api/practice-cases'
import type { PracticeCase } from '@/api/practice-cases'

interface ArticleJson {
  title?: string
  text?: string
  author?: string
  url?: string
  sourceType?: string
  sentiment?: 'positive' | 'neutral' | 'negative'
  summary?: string
  content?: string
  published_at?: string
  publishedAt?: string
  sourceName?: string
  timestamp?: string
  [key: string]: unknown
}

interface TimelineEvent {
  eventId: string
  rowIndex: number
  absoluteTimestamp: string
  relativePosition: number
  description: string
  communicationFocus: string | null
  stakeholderUpdates: Record<string, string>
}

interface ViewPracticeCaseDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  practiceCase: PracticeCase | null
}

const getStatusBadge = (status: string) => {
  switch (status) {
    case 'ready':
      return (
        <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300">
          <CheckCircle className="h-3 w-3 mr-1" />
          Klaar
        </Badge>
      )
    case 'generating':
      return (
        <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-300">
          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
          Genereren...
        </Badge>
      )
    case 'queued':
      return (
        <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-300">
          <Clock className="h-3 w-3 mr-1" />
          In wachtrij
        </Badge>
      )
    case 'error':
      return (
        <Badge variant="outline" className="bg-red-100 text-red-800 border-red-300">
          <AlertCircle className="h-3 w-3 mr-1" />
          Fout
        </Badge>
      )
    default:
      return <Badge variant="outline">{status}</Badge>
  }
}

const getSentimentBadge = (sentiment: string | undefined) => {
  if (!sentiment) return null
  const sentimentStr = typeof sentiment === 'string' ? sentiment.toLowerCase() : ''
  switch (sentimentStr) {
    case 'positive':
      return (
        <Badge className="bg-green-100 text-green-800 border-green-300">
          Positief
        </Badge>
      )
    case 'negative':
      return (
        <Badge className="bg-red-100 text-red-800 border-red-300">
          Negatief
        </Badge>
      )
    case 'neutral':
      return (
        <Badge className="bg-gray-100 text-gray-800 border-gray-300">
          Neutraal
        </Badge>
      )
    default:
      return null
  }
}

const getSourceTypeIcon = (sourceType: string | undefined): string => {
  if (!sourceType || typeof sourceType !== 'string') return '📝'
  switch (sourceType.toLowerCase()) {
    case 'twitter':
      return '𝕏'
    case 'facebook':
      return 'f'
    case 'news':
      return '📰'
    case 'blog':
      return '✍️'
    case 'reddit':
      return '🔴'
    case 'forum':
      return '💬'
    default:
      return '📝'
  }
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

export function ViewPracticeCaseDialog({
  open,
  onOpenChange,
  practiceCase,
}: ViewPracticeCaseDialogProps) {
  const { data: articlesData, isLoading: articlesLoading } = usePracticeCaseArticles(
    practiceCase?.id || null
  )

  // Create a lookup map for timeline events
  const timelineEventsMap = new Map<string, TimelineEvent>()
  if (practiceCase?.timelineMetadata?.events) {
    practiceCase.timelineMetadata.events.forEach((event) => {
      timelineEventsMap.set(event.eventId, event)
    })
  }

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '-'
    return formatDistanceToNow(new Date(dateString), {
      addSuffix: true,
      locale: nl,
    })
  }

  const formatDateTime = (dateString: string | null): string => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleString('nl-NL')
  }

  const formatTime = (timestamp: string): string => {
    return new Date(timestamp).toLocaleTimeString('nl-NL', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Oefenscenario Details
          </DialogTitle>
        </DialogHeader>

        {practiceCase && (
          <div className="max-h-[75vh] overflow-y-auto pr-4">
            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="overview">Overzicht</TabsTrigger>
                <TabsTrigger value="timeline">Tijdlijn</TabsTrigger>
                <TabsTrigger value="messages">Berichten</TabsTrigger>
                <TabsTrigger value="files">Bestanden</TabsTrigger>
              </TabsList>
              <TabsContent value="overview" className="space-y-6">
                {/* Basic Information */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Basisinformatie</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-muted-foreground">Titel</Label>
                        <p className="font-medium text-base mt-1">{practiceCase.title}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Locatie</Label>
                        <p className="font-medium text-base mt-1">{practiceCase.location}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Status</Label>
                        <div className="mt-2">{getStatusBadge(practiceCase.status)}</div>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Totale Artikelen</Label>
                        <p className="font-medium text-base mt-1">{practiceCase.articleCount}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Gemaakt door</Label>
                        <p className="font-medium text-base mt-1 flex items-center gap-2">
                          <User className="h-4 w-4" />
                          {practiceCase.createdBy?.name || '-'}
                        </p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Gemaakt op</Label>
                        <p className="font-medium text-base mt-1 flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          {formatDateTime(practiceCase.createdAt)}
                        </p>
                      </div>
                    </div>

                    {practiceCase.status === 'error' && practiceCase.errorMessage && (
                      <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-md p-3 mt-4">
                        <p className="text-sm text-red-600 dark:text-red-400">
                          <strong>Fout:</strong> {practiceCase.errorMessage}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Generation Information */}
                {(practiceCase.generationStartedAt ||
                  practiceCase.generationCompletedAt ||
                  practiceCase.generationModel) && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Zap className="h-5 w-5" />
                        Generatie Informatie
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {practiceCase.generationStartedAt && (
                          <div>
                            <Label className="text-muted-foreground">Gestart op</Label>
                            <p className="font-medium text-sm mt-1">
                              {formatDateTime(practiceCase.generationStartedAt)}
                            </p>
                          </div>
                        )}
                        {practiceCase.generationCompletedAt && (
                          <div>
                            <Label className="text-muted-foreground">Voltooid op</Label>
                            <p className="font-medium text-sm mt-1">
                              {formatDateTime(practiceCase.generationCompletedAt)}
                            </p>
                          </div>
                        )}
                        {practiceCase.generationModel && (
                          <div>
                            <Label className="text-muted-foreground">AI Model</Label>
                            <p className="font-medium text-sm mt-1">{practiceCase.generationModel}</p>
                          </div>
                        )}
                        {practiceCase.generationTokens && (
                          <div>
                            <Label className="text-muted-foreground">Tokens Gebruikt</Label>
                            <p className="font-medium text-sm mt-1">
                              {practiceCase.generationTokens.toLocaleString()}
                            </p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="timeline" className="space-y-6">
                {practiceCase.crisisDate || practiceCase.timelineMetadata ? (
                  <>
                    {/* Timeline Duration Information */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Clock className="h-5 w-5" />
                          Tijdlijn
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {practiceCase.crisisDate && (
                            <div>
                              <Label className="text-muted-foreground">Crisis Datum</Label>
                              <p className="font-medium text-base mt-1">
                                {formatDate(practiceCase.crisisDate)}
                              </p>
                            </div>
                          )}
                          {practiceCase.crisisStartTime && (
                            <div>
                              <Label className="text-muted-foreground">Starttijd</Label>
                              <p className="font-medium text-base mt-1">
                                {practiceCase.crisisStartTime}
                              </p>
                            </div>
                          )}
                          {practiceCase.crisisEndTime && (
                            <div>
                              <Label className="text-muted-foreground">Eindtijd</Label>
                              <p className="font-medium text-base mt-1">
                                {practiceCase.crisisEndTime}
                              </p>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Timeline Metadata */}
                    {practiceCase.timelineMetadata && (
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Database className="h-5 w-5" />
                            Generatie Statistieken
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label className="text-muted-foreground">Totale Gebeurtenissen</Label>
                              <p className="font-medium text-base mt-1">
                                {practiceCase.timelineMetadata.eventCount}
                              </p>
                            </div>
                            <div>
                              <Label className="text-muted-foreground">Duur (Minuten)</Label>
                              <p className="font-medium text-base mt-1">
                                {practiceCase.timelineMetadata.durationMinutes} min
                              </p>
                            </div>
                            <div>
                              <Label className="text-muted-foreground">Batch Aantal</Label>
                              <p className="font-medium text-base mt-1">
                                {practiceCase.timelineMetadata.batchCount}
                              </p>
                            </div>
                            <div>
                              <Label className="text-muted-foreground">Gebeurtenissen met Data</Label>
                              <p className="font-medium text-base mt-1">
                                {practiceCase.timelineMetadata.events?.length || 0}
                              </p>
                            </div>
                          </div>

                          {/* Stakeholders from events */}
                          {practiceCase.timelineMetadata.events &&
                            practiceCase.timelineMetadata.events.length > 0 && (
                              <div className="mt-4 pt-4 border-t">
                                <Label className="text-muted-foreground">
                                  Betrokken Partijen (uit gebeurtenissen)
                                </Label>
                                <div className="flex flex-wrap gap-2 mt-2">
                                  {Array.from(
                                    new Set(
                                      practiceCase.timelineMetadata.events.flatMap((event) =>
                                        Object.keys(event.stakeholderUpdates || {})
                                      )
                                    )
                                  ).map((stakeholder, index) => (
                                    <Badge key={index} variant="outline">
                                      <Users className="h-3 w-3 mr-1" />
                                      {stakeholder}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                        </CardContent>
                      </Card>
                    )}

                    {/* Detailed Timeline Events */}
                    {practiceCase.timelineMetadata?.events &&
                      practiceCase.timelineMetadata.events.length > 0 && (
                        <Card>
                          <CardHeader>
                            <CardTitle className="text-lg flex items-center gap-2">
                              <ActivitySquare className="h-5 w-5" />
                              Gebeurtenissen ({practiceCase.timelineMetadata.events.length})
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-4">
                              {practiceCase.timelineMetadata.events
                                .sort(
                                  (a, b) =>
                                    new Date(a.absoluteTimestamp).getTime() -
                                    new Date(b.absoluteTimestamp).getTime()
                                )
                                .map((event, index) => (
                                  <Card key={event.eventId} className="bg-muted/20">
                                    <CardContent className="pt-4 space-y-3">
                                      <div className="flex items-start justify-between gap-4">
                                        <div className="flex items-center gap-3">
                                          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                                            <span className="text-sm font-bold text-primary">
                                              {index + 1}
                                            </span>
                                          </div>
                                          <div>
                                            <div className="flex items-center gap-2">
                                              <Clock className="h-4 w-4 text-muted-foreground" />
                                              <span className="text-sm font-semibold">
                                                {formatTime(event.absoluteTimestamp)}
                                              </span>
                                            </div>
                                            <span className="text-xs text-muted-foreground">
                                              Rij {event.rowIndex}
                                            </span>
                                          </div>
                                        </div>
                                        <Badge variant="secondary" className="text-xs">
                                          {event.eventId}
                                        </Badge>
                                      </div>

                                      <div className="pl-11">
                                        <p className="text-sm leading-relaxed">
                                          {event.description}
                                        </p>
                                      </div>

                                      {event.communicationFocus && (
                                        <div className="pl-11 pt-2 border-t">
                                          <Label className="text-muted-foreground text-xs flex items-center gap-1">
                                            <MessageSquare className="h-3 w-3" />
                                            Communicatie Focus
                                          </Label>
                                          <p className="text-xs mt-1 text-muted-foreground italic">
                                            {event.communicationFocus}
                                          </p>
                                        </div>
                                      )}

                                      {event.stakeholderUpdates &&
                                        Object.keys(event.stakeholderUpdates).length > 0 && (
                                          <div className="pl-11 pt-2 border-t">
                                            <Label className="text-muted-foreground text-xs flex items-center gap-1">
                                              <Users className="h-3 w-3" />
                                              Acties per Partij
                                            </Label>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                                              {Object.entries(event.stakeholderUpdates).map(
                                                ([stakeholder, update]) => (
                                                  <div
                                                    key={stakeholder}
                                                    className="flex flex-col gap-1 p-2 bg-background rounded border"
                                                  >
                                                    <Badge
                                                      variant="outline"
                                                      className="text-xs w-fit"
                                                    >
                                                      {stakeholder}
                                                    </Badge>
                                                    <span className="text-xs text-muted-foreground">
                                                      {update}
                                                    </span>
                                                  </div>
                                                )
                                              )}
                                            </div>
                                          </div>
                                        )}
                                    </CardContent>
                                  </Card>
                                ))}
                            </div>
                          </CardContent>
                        </Card>
                      )}
                  </>
                ) : (
                  <Card>
                    <CardContent className="pt-6">
                      <p className="text-muted-foreground text-center">
                        Geen tijdlijn informatie beschikbaar
                      </p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="messages" className="space-y-4">
                {articlesLoading ? (
                  <Card>
                    <CardContent className="pt-6 flex justify-center items-center">
                      <Loader2 className="h-6 w-6 animate-spin" />
                      <span className="ml-2">Berichten laden...</span>
                    </CardContent>
                  </Card>
                ) : articlesData?.data?.length === 0 ? (
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-center py-8 text-muted-foreground">
                        <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>Geen berichten gevonden</p>
                        <p className="text-sm mt-2">
                          {practiceCase.status === 'ready'
                            ? 'Er zijn nog geen artikelen gegenereerd voor dit scenario.'
                            : 'Artikelen worden nog gegenereerd.'}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {articlesData?.data?.map((article) => (
                      <Card key={article.id}>
                        <CardHeader>
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <CardTitle className="text-base leading-relaxed">
                                {(article.articleJson as ArticleJson)?.title || 'Geen titel'}
                              </CardTitle>
                              <div className="flex flex-wrap gap-2 mt-3">
                                {(article.articleJson as ArticleJson)?.sourceType && (
                                  <Badge variant="secondary">
                                    {getSourceTypeIcon(
                                      typeof (article.articleJson as ArticleJson)?.sourceType ===
                                      'string'
                                        ? (article.articleJson as ArticleJson)?.sourceType
                                        : undefined
                                    )}
                                    {typeof (article.articleJson as ArticleJson)?.sourceType ===
                                    'string'
                                      ? (article.articleJson as ArticleJson)?.sourceType
                                      : 'Onbekend'}
                                  </Badge>
                                )}
                                {(article.articleJson as ArticleJson)?.sentiment &&
                                  typeof (article.articleJson as ArticleJson)?.sentiment ===
                                    'string' && (
                                    <div>
                                      {getSentimentBadge(
                                        (article.articleJson as ArticleJson)?.sentiment as string
                                      )}
                                    </div>
                                  )}
                                {article.syntheticFlag && (
                                  <Badge variant="outline" className="text-xs">
                                    Synthetisch
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          {/* Author Information */}
                          {(article.articleJson as ArticleJson)?.author &&
                            typeof (article.articleJson as ArticleJson)?.author === 'string' && (
                              <div className="flex items-center gap-2 pb-2 border-b">
                                <User className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm">
                                  <span className="font-medium">
                                    {(article.articleJson as ArticleJson)?.author}
                                  </span>
                                </span>
                              </div>
                            )}

                          {/* Article Text/Content */}
                          {(article.articleJson as ArticleJson)?.text &&
                            typeof (article.articleJson as ArticleJson)?.text === 'string' && (
                              <div>
                                <Label className="text-muted-foreground text-xs">Inhoud</Label>
                                <p className="text-sm mt-2 leading-relaxed whitespace-pre-wrap">
                                  {(article.articleJson as ArticleJson)?.text}
                                </p>
                              </div>
                            )}

                          {/* URL Link */}
                          {(article.articleJson as ArticleJson)?.url &&
                            typeof (article.articleJson as ArticleJson)?.url === 'string' && (
                              <div className="flex items-start gap-2 pt-2 border-t">
                                <Link2 className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                                <a
                                  href={(article.articleJson as ArticleJson)?.url as string}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline break-all"
                                >
                                  {(article.articleJson as ArticleJson)?.url}
                                </a>
                              </div>
                            )}

                          {/* Timeline Event Details */}
                          {article.timelineEventIds && article.timelineEventIds.length > 0 && (
                            <div className="pt-2 border-t space-y-3">
                              <Label className="text-muted-foreground text-xs">
                                Gerelateerde Tijdlijn Gebeurtenissen
                              </Label>
                              {article.timelineEventIds.map((eventId, idx) => {
                                const event = timelineEventsMap.get(eventId)
                                if (!event) {
                                  // Fallback if event not found in map
                                  return (
                                    <Badge key={idx} variant="outline" className="text-xs">
                                      <ActivitySquare className="h-3 w-3 mr-1" />
                                      {eventId}
                                    </Badge>
                                  )
                                }
                                return (
                                  <Card key={idx} className="bg-muted/30">
                                    <CardContent className="pt-4 space-y-2">
                                      <div className="flex items-start justify-between gap-2">
                                        <div className="flex items-center gap-2 flex-1">
                                          <Clock className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                          <span className="text-sm font-medium">
                                            {formatTime(event.absoluteTimestamp)}
                                          </span>
                                        </div>
                                        <Badge variant="secondary" className="text-xs">
                                          <ActivitySquare className="h-3 w-3 mr-1" />
                                          Rij {event.rowIndex}
                                        </Badge>
                                      </div>
                                      <div>
                                        <p className="text-sm leading-relaxed">{event.description}</p>
                                      </div>
                                      {event.communicationFocus && (
                                        <div className="pt-2 border-t">
                                          <Label className="text-muted-foreground text-xs">
                                            Communicatie Focus
                                          </Label>
                                          <p className="text-xs mt-1 text-muted-foreground">
                                            {event.communicationFocus}
                                          </p>
                                        </div>
                                      )}
                                      {event.stakeholderUpdates &&
                                        Object.keys(event.stakeholderUpdates).length > 0 && (
                                          <div className="pt-2 border-t">
                                            <Label className="text-muted-foreground text-xs">
                                              Betrokken Partijen
                                            </Label>
                                            <div className="grid grid-cols-1 gap-2 mt-2">
                                              {Object.entries(event.stakeholderUpdates).map(
                                                ([stakeholder, update]) => (
                                                  <div key={stakeholder} className="flex gap-3">
                                                    <strong className="text-xs">
                                                      {stakeholder}:
                                                    </strong>
                                                    <span className="text-xs text-muted-foreground">
                                                      {update}
                                                    </span>
                                                  </div>
                                                )
                                              )}
                                            </div>
                                          </div>
                                        )}
                                    </CardContent>
                                  </Card>
                                )
                              })}
                            </div>
                          )}

                          {/* Metadata */}
                          <div className="pt-2 border-t text-xs text-muted-foreground">
                            {formatDate(article.createdAt)}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="files" className="space-y-4">
                {practiceCase.fileMetadata && practiceCase.fileMetadata.length > 0 ? (
                  <div className="space-y-3">
                    {practiceCase.fileMetadata.map((file, index) => (
                      <Card key={index}>
                        <CardContent className="pt-6">
                          <div className="flex items-center gap-4">
                            <FileIcon className="h-8 w-8 text-muted-foreground flex-shrink-0" />
                            <div className="flex-1">
                              <p className="font-medium text-sm">{file.originalFilename}</p>
                              <p className="text-xs text-muted-foreground mt-1">
                                {file.fileType} • {formatFileSize(file.fileSize)} • Geüpload{' '}
                                {formatDate(file.uploadedAt)}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-center py-8 text-muted-foreground">
                        <FileIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>Geen bestanden beschikbaar</p>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            </Tabs>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Sluiten
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
